import React, { useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Button,
    Stack,
} from '@mui/material';
import {
    Add,
    SmartToy
} from '@mui/icons-material';
import Search from '@/components/Search';
import { Notification } from '@/components/Notification';
import HelperView from './helper';
import { DeleteHelper, HelperSummaryCard } from '@/components/helper';
import ListTitle from '@/components/ListTitle';
import { useTheme } from '@mui/material/styles';
import { useContext } from 'react';
import { HelpersContext } from '@my-helpers/common';

interface HelpersViewProps {
    creating: boolean;
    setCreating: (creating: boolean) => void;
}

const HelpersView: React.FC<HelpersViewProps> = ({ creating, setCreating }) => {
    const context = useContext(HelpersContext);
    if (context === undefined) {
        throw new Error('useHelpersContext must be used within a HelpersProvider');
    }

    const { view, command } = context;
    const { helpers, error, isLoading } = view;
    const theme = useTheme();
    const [deleting, setDeleting] = useState<string | undefined>(undefined);

    if (creating) {
        return (
            <HelperView 
                creating={creating} 
                setCreating={setCreating} />
        );
    }

    return (
        <Stack padding={theme.spacing(3)}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mb: 3
            }}>
                <ListTitle title="Helpers" icon={SmartToy} />
                <Search placeholder="Search helpers..." />
                <Box sx={{
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'flex-end',
                }} >
                    <Tooltip title="Create New Helper" arrow>
                        <IconButton
                            color="primary"
                            onClick={() => {
                                setCreating(true)
                            }}
                            sx={{
                                backgroundColor: 'primary.main',
                                color: 'white',
                                flexShrink: 0,
                                '&:hover': {
                                    backgroundColor: 'primary.dark',
                                }
                            }}
                        >
                            <Add />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box> 
            {
                helpers && helpers.length === 0 ? 
                    <Box sx={{
                        textAlign: 'center',
                        py: 4
                    }}>
                        <Typography
                            variant="h6"
                            color="text.secondary"
                            gutterBottom
                        >
                            No helpers found
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            Create your first helper to get started
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => setCreating(true)}
                        >
                            Create Helper
                        </Button>
                    </Box> :
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(12, 1fr)',
                            gap: 2
                        }}>
                        {
                            helpers?.map((helper) =>
                                <Box
                                    key={helper.id}
                                    sx={{
                                        gridColumn: 'span 4',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: 2
                                    }}>
                                    <HelperSummaryCard
                                        helper={helper}
                                        setDeleting={setDeleting} />
                                </Box>
                            )
                        }
                    </Box>
            }
            <DeleteHelper 
                helperId={deleting} 
                helperName={helpers?.find((helper) => helper.id === deleting)?.name || ''}
                deleteHelper={command.deleteHelper} 
                setDeleting={setDeleting} />
            <Notification
                isLoading={isLoading}
                error={error}
                clearError={command.clearError}
                delay={300}
                autoHideDuration={1200}
            />
        </Stack>
    );
};

export default HelpersView;
